import type { MaterialNames } from "@/components/material_symbol_names";
import { DATE_ONLY_PATTERN, EXPLICIT_TIMEZONE_PATTERN } from "@/lib/dateHelpers";
import type { TimelineEmployment, TimelinePerson } from "./queries";
import { DateTime } from "luxon";

export type TimelineCategory = "Membership" | "Section" | "Education" | "Career" | "Prospect";

export type TimelineMilestone = {
  id: string;
  date: string;
  endDate?: string | null;
  category: TimelineCategory;
  title: string;
  description?: string | null;
  iconName: MaterialNames;
  status?: string | null;
  link?: string;
};

export type GroupedTimelineMilestones = {
  year: string;
  milestones: TimelineMilestone[];
};

export type TimelineActivityDay = {
  date: string;
  dayIndex: number;
  weekIndex: number;
  isCurrentYear: boolean;
  events: TimelineMilestone[];
  intensity: 0 | 1 | 2 | 3 | 4;
};

export type TimelineActivityYear = {
  year: number;
  weeks: TimelineActivityDay[][];
  monthLabels: Array<{
    label: string;
    weekIndex: number;
  }>;
};

const personPath = (pgaId: string | null | undefined, suffix: string) =>
  pgaId ? `/people/${pgaId}${suffix}` : undefined;

const isValidDate = (date: string | null | undefined) => !!date && DateTime.fromISO(date).isValid;

const hasOccurred = (date: string | null | undefined) => {
  if (!date || !isValidDate(date)) return false;
  return DateTime.fromISO(date).startOf("day") <= DateTime.now().startOf("day");
};

const formatLabel = (value: string | null | undefined) => {
  if (!value) return null;

  return value
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const pushMilestone = (milestones: TimelineMilestone[], milestone: TimelineMilestone) => {
  if (isValidDate(milestone.date)) {
    milestones.push(milestone);
  }
};

const pushOccurredMilestone = (milestones: TimelineMilestone[], milestone: TimelineMilestone) => {
  if (hasOccurred(milestone.date)) {
    milestones.push(milestone);
  }
};

const employmentTitle = (employment: TimelineEmployment) =>
  employment.formattedTitle || employment.title || "Employment";

export function buildTimelineMilestones(person: TimelinePerson): TimelineMilestone[] {
  const milestones: TimelineMilestone[] = [];
  const pgaId = person.pgaId;

  pushMilestone(milestones, {
    id: "account-created",
    date: person.accountCreatedAt || "",
    category: "Prospect",
    title: "PGA account created",
    description: "Initial account creation milestone.",
    iconName: "person_add" as MaterialNames,
    link: personPath(pgaId, "/membership/prospect-program")
  });

  person.bands?.forEach((band) => {
    const programName = band.programType?.name || "Membership";
    const classification = band.classification?.name;
    const sectionName = band.sectionAffiliation?.section?.name || band.section?.name;

    pushMilestone(milestones, {
      id: `band-${band.id}-started`,
      date: band.startedOn,
      endDate: band.expiresOn,
      category: "Membership",
      title: `${programName}${classification ? ` - ${classification}` : ""} started`,
      description: sectionName ? `Section: ${sectionName}` : null,
      iconName: "badge" as MaterialNames,
      status: formatLabel(band.createdReason),
      link: personPath(pgaId, "/membership/overview")
    });

    pushOccurredMilestone(milestones, {
      id: `band-${band.id}-terminated`,
      date: band.terminatesOn || "",
      category: "Membership",
      title: `${programName}${classification ? ` - ${classification}` : ""} terminated`,
      description: sectionName ? `Section: ${sectionName}` : null,
      iconName: "block" as MaterialNames,
      status: formatLabel(band.terminatedReason),
      link: personPath(pgaId, "/membership/overview")
    });

    if (band.expiresOn && band.expiresOn !== band.terminatesOn) {
      pushOccurredMilestone(milestones, {
        id: `band-${band.id}-expired`,
        date: band.expiresOn,
        category: "Membership",
        title: `${programName}${classification ? ` - ${classification}` : ""} ended`,
        description: sectionName ? `Section: ${sectionName}` : null,
        iconName: "event_busy" as MaterialNames,
        link: personPath(pgaId, "/membership/overview")
      });
    }
  });

  person.sectionAffiliations?.forEach((affiliation) => {
    const sectionName = affiliation.section?.name || "section";

    pushMilestone(milestones, {
      id: `section-${affiliation.id}-started`,
      date: affiliation.startAt,
      endDate: affiliation.endAt,
      category: "Section",
      title: `Joined ${sectionName}`,
      description: affiliation.primary ? "Primary section affiliation" : "Section affiliation",
      iconName: "location_on" as MaterialNames,
      link: personPath(pgaId, "/membership/section")
    });

    pushOccurredMilestone(milestones, {
      id: `section-${affiliation.id}-ended`,
      date: affiliation.endAt || "",
      category: "Section",
      title: `Left ${sectionName}`,
      description: affiliation.primary ? "Primary section affiliation ended" : "Section affiliation ended",
      iconName: "location_off" as MaterialNames,
      link: personPath(pgaId, "/membership/section")
    });
  });

  person.employments?.forEach((employment) => {
    const facilityName = employment.facility?.name || "facility";
    const title = employmentTitle(employment);

    pushMilestone(milestones, {
      id: `employment-${employment.id}-started`,
      date: employment.startedOn || "",
      endDate: employment.endedOn,
      category: "Career",
      title: `Started ${title}`,
      description: `At ${facilityName}${employment.jobClassification ? ` - ${employment.jobClassification}` : ""}`,
      iconName: "business_center" as MaterialNames,
      status: employment.isPrimary ? "Primary" : null,
      link: personPath(pgaId, "/career/employment")
    });

    pushOccurredMilestone(milestones, {
      id: `employment-${employment.id}-ended`,
      date: employment.endedOn || "",
      category: "Career",
      title: `Ended ${title}`,
      description: `At ${facilityName}`,
      iconName: "work_history" as MaterialNames,
      link: personPath(pgaId, "/career/employment")
    });
  });

  person.ccmsSuspensions?.forEach((suspension) => {
    pushMilestone(milestones, {
      id: `suspension-${suspension.id}-started`,
      date: suspension.startDate,
      endDate: suspension.endDate,
      category: "Membership",
      title: "Suspension started",
      description: suspension.reasonDescription || suspension.memo || suspension.code,
      iconName: "pause_circle" as MaterialNames,
      status: suspension.code,
      link: personPath(pgaId, "/membership/overview?scrollTo=suspension-history")
    });

    pushOccurredMilestone(milestones, {
      id: `suspension-${suspension.id}-ended`,
      date: suspension.endDate || "",
      category: "Membership",
      title: "Suspension ended",
      description: suspension.reasonDescription || suspension.memo || suspension.code,
      iconName: "play_circle" as MaterialNames,
      status: suspension.code,
      link: personPath(pgaId, "/membership/overview?scrollTo=suspension-history")
    });
  });

  const prospectProgram = person.prospectProgram;
  if (prospectProgram?.membershipInterestForm) {
    pushMilestone(milestones, {
      id: `interest-form-${prospectProgram.membershipInterestForm.id}`,
      date: prospectProgram.membershipInterestForm.startedAt,
      category: "Prospect",
      title: "Membership interest form submitted",
      description: prospectProgram.membershipInterestForm.reasonForBecomingMember,
      iconName: "assignment" as MaterialNames,
      status: "Complete",
      link: personPath(pgaId, "/membership/prospect-program")
    });
  }

  if (prospectProgram?.eligibleEmployment) {
    pushMilestone(milestones, {
      id: `eligible-employment-${prospectProgram.eligibleEmployment.id}`,
      date: prospectProgram.eligibleEmployment.startDate || "",
      endDate: prospectProgram.eligibleEmployment.endDate,
      category: "Prospect",
      title: "Eligible employment started",
      description: "Employment requirement for the prospect program.",
      iconName: "task_alt" as MaterialNames,
      status: "Complete",
      link: personPath(pgaId, "/membership/prospect-program")
    });
  }

  if (prospectProgram?.backgroundCheck) {
    pushMilestone(milestones, {
      id: "background-check",
      date: prospectProgram.backgroundCheck.startDate || "",
      endDate: prospectProgram.backgroundCheck.expirationDate,
      category: "Prospect",
      title: "Background check",
      description: "Prospect program background check.",
      iconName: "verified_user" as MaterialNames,
      status: formatLabel(prospectProgram.backgroundCheck.status),
      link: personPath(pgaId, "/membership/prospect-program")
    });
  }

  if (prospectProgram?.qualifyingLevel) {
    pushMilestone(milestones, {
      id: "qualifying-level",
      date: prospectProgram.qualifyingLevel.endDate || prospectProgram.qualifyingLevel.startDate || "",
      endDate: prospectProgram.qualifyingLevel.expirationDate,
      category: "Prospect",
      title: prospectProgram.qualifyingLevel.completed ? "Qualifying level completed" : "Qualifying level started",
      description: "Qualifying level coursework milestone.",
      iconName: "school" as MaterialNames,
      status: formatLabel(prospectProgram.qualifyingLevel.status),
      link: personPath(pgaId, "/membership/prospect-program")
    });
  }

  const pat = prospectProgram?.playingAbilityTestStatus;
  pat?.submissions?.forEach((submission, index) => {
    pushMilestone(milestones, {
      id: `pat-submission-${submission.id || index}`,
      date: submission.date,
      endDate: submission.expirationDate,
      category: "Prospect",
      title: "Playing Ability Test submitted",
      description: submission.patType,
      iconName: "sports_golf" as MaterialNames,
      status: formatLabel(submission.result),
      link: personPath(pgaId, "/membership/prospect-program?scrollTo=pat-activity-table")
    });
  });

  if (pat && !pat.submissions?.length) {
    pushMilestone(milestones, {
      id: "pat-status",
      date: pat.startDate || "",
      endDate: pat.expirationDate,
      category: "Prospect",
      title: "Playing Ability Test",
      description: "PAT status milestone.",
      iconName: "sports_golf" as MaterialNames,
      status: formatLabel(pat.status),
      link: personPath(pgaId, "/membership/prospect-program?scrollTo=pat-activity-table")
    });
  }

  person.educationPgmProgramHistory?.forEach((program, index) => {
    const level = program.levelCode || `Level ${program.level}`;
    const programType = program.programType || "PGM Program";

    pushMilestone(milestones, {
      id: `pgm-${program.curriculumVersion}-${level}-${index}-started`,
      date: program.startDate,
      endDate: program.endDate,
      category: "Education",
      title: `${programType} ${level} started`,
      description: program.careerPath || `Curriculum ${program.curriculumVersion}`,
      iconName: "school" as MaterialNames,
      link: personPath(pgaId, "/education/professional-development")
    });

    pushOccurredMilestone(milestones, {
      id: `pgm-${program.curriculumVersion}-${level}-${index}-completed`,
      date: program.endDate || "",
      category: "Education",
      title: `${programType} ${level} completed`,
      description: program.careerPath || `Curriculum ${program.curriculumVersion}`,
      iconName: "workspace_premium" as MaterialNames,
      status: program.completed ? "Complete" : null,
      link: personPath(pgaId, "/education/professional-development")
    });

    program.testHistory?.forEach((test, testIndex) => {
      pushMilestone(milestones, {
        id: `pgm-test-${program.curriculumVersion}-${level}-${testIndex}`,
        date: test.testDate,
        category: "Education",
        title: test.testDefinition?.description || "PGM test completed",
        description: `Attempt ${test.attemptNumber}`,
        iconName: "quiz" as MaterialNames,
        status: formatLabel(test.completionStatus),
        link: personPath(pgaId, "/education/professional-development")
      });
    });
  });

  if (person.educationLatestCertification) {
    const certification = person.educationLatestCertification;
    pushMilestone(milestones, {
      id: "certification-started",
      date: certification.startDate,
      endDate: certification.endDate,
      category: "Education",
      title: `${certification.programDescription} started`,
      description: certification.careerPath,
      iconName: "workspace_premium" as MaterialNames,
      link: personPath(pgaId, "/education/credentials")
    });

    pushOccurredMilestone(milestones, {
      id: "certification-completed",
      date: certification.endDate || "",
      category: "Education",
      title: `${certification.programDescription} completed`,
      description: certification.careerPath,
      iconName: "verified" as MaterialNames,
      status: "Complete",
      link: personPath(pgaId, "/education/credentials")
    });
  }

  return milestones.sort((a, b) => DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis());
}

export function groupTimelineMilestones(milestones: TimelineMilestone[]): GroupedTimelineMilestones[] {
  const groups = milestones.reduce<Record<string, TimelineMilestone[]>>((acc, milestone) => {
    const year = DateTime.fromISO(milestone.date).isValid
      ? DateTime.fromISO(milestone.date).toFormat("yyyy")
      : "Unknown";
    acc[year] = [...(acc[year] || []), milestone];
    return acc;
  }, {});

  return Object.entries(groups)
    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
    .map(([year, groupMilestones]) => ({ year, milestones: groupMilestones }));
}

const getSundayBasedDayIndex = (date: DateTime) => date.weekday % 7;

const getActivityIntensity = (eventCount: number): TimelineActivityDay["intensity"] => {
  if (eventCount <= 0) return 0;
  if (eventCount === 1) return 1;
  if (eventCount === 2) return 2;
  if (eventCount === 3) return 3;
  return 4;
};

const getTimelineActivityDateKey = (date: string) => {
  if (DATE_ONLY_PATTERN.test(date)) {
    return date;
  }

  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }

  return EXPLICIT_TIMEZONE_PATTERN.test(date) ? parsedDate.toUTC().toISODate() : parsedDate.toISODate();
};

function buildActivityYear(year: number, eventsByDate: Map<string, TimelineMilestone[]>): TimelineActivityYear {
  const firstDay = DateTime.fromObject({ year, month: 1, day: 1 }, { zone: "utc" });
  const lastDay = DateTime.fromObject({ year, month: 12, day: 31 }, { zone: "utc" });
  const calendarStart = firstDay.minus({ days: getSundayBasedDayIndex(firstDay) });
  const calendarEnd = lastDay.plus({ days: 6 - getSundayBasedDayIndex(lastDay) });
  const weeks: TimelineActivityDay[][] = [];

  let cursor = calendarStart;
  let weekIndex = 0;

  while (cursor <= calendarEnd) {
    const week: TimelineActivityDay[] = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const dateKey = cursor.toISODate() || "";
      const events = eventsByDate.get(dateKey) || [];

      week.push({
        date: dateKey,
        dayIndex,
        weekIndex,
        isCurrentYear: cursor.year === year,
        events,
        intensity: getActivityIntensity(events.length)
      });

      cursor = cursor.plus({ days: 1 });
    }

    weeks.push(week);
    weekIndex += 1;
  }

  return {
    year,
    weeks,
    monthLabels: Array.from({ length: 12 }, (_, index) => {
      const monthStart = DateTime.fromObject({ year, month: index + 1, day: 1 }, { zone: "utc" });
      const weekIndexForMonth = Math.floor(monthStart.diff(calendarStart, "days").days / 7);

      return {
        label: monthStart.toFormat("LLL"),
        weekIndex: weekIndexForMonth
      };
    })
  };
}

export function buildTimelineActivityYears(milestones: TimelineMilestone[]): TimelineActivityYear[] {
  const eventsByDate = milestones.reduce<Map<string, TimelineMilestone[]>>((acc, milestone) => {
    const dateKey = getTimelineActivityDateKey(milestone.date);

    if (dateKey) {
      acc.set(dateKey, [...(acc.get(dateKey) || []), milestone]);
    }

    return acc;
  }, new Map());

  const years = Array.from(
    new Set(
      milestones
        .map((milestone) => getTimelineActivityDateKey(milestone.date))
        .filter((dateKey): dateKey is string => !!dateKey)
        .map((dateKey) => Number(dateKey.slice(0, 4)))
    )
  ).sort((a, b) => b - a);

  return years.map((year) => buildActivityYear(year, eventsByDate));
}
